import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import {
  PRICE_LIST_CATEGORY_LABELS,
  formatPriceListPrice,
  isPriced,
} from "@xop/dashboard-shared";
import { GitBranch, Pencil, Trash2 } from "lucide-react";
import type { JSX } from "react";

import type { ProductFeature } from "../products-api.js";
import { featureGridTemplate } from "./featureColumns.js";

/**
 * One feature inside an expanded product, as a row of COLUMNS: name, part
 * number, category, price.
 *
 * It used to be a single flex line with the part number and category trailing
 * the name as inline chips, which meant neither ever lined up between one
 * feature and the next — the eye had to re-find them per row. A product holds
 * 25 of these and the reason to open it is to compare them, so the columns
 * are the feature. The template comes from `featureColumns.ts` so the header
 * above cannot drift out of alignment with the rows below it.
 *
 * There is nothing to expand any more: features had versions under them until
 * 2026-09-13 and the row was a disclosure triangle over a list of numbers.
 * What an operator actually comes here to read is what the thing costs, so
 * that is what the row says.
 *
 * "No price set" is deliberately not "$0.00". A feature nobody has priced yet
 * and a feature that is genuinely free ("Included") are different statements,
 * and only one of them is a decision.
 *
 * LINKED / NOT LINKED (2026-09-15). A Value Added Service or Platform
 * Extension goes on a licence only through the appliance key it is linked to
 * (`licence_key`). An unlinked one is sold and cannot be licensed, which is a
 * fact the operator must be able to see from the list — so the chip is per
 * row, and only on the two licensable categories.
 */
const LICENSABLE_CATEGORIES = new Set(["value_added_service", "platform_extension"]);

export function FeatureRow(props: {
  feature: ProductFeature;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { feature, canEdit } = props;
  const priced = feature.priced ?? isPriced(feature);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: featureGridTemplate(canEdit),
        alignItems: "center",
        gap: 1.5,
        borderRadius: 0.5,
        bgcolor: "surfaceSoft",
        px: 1,
        py: 0.75,
        // A retired feature stays listed — documents still name it — but it
        // must not read as something you can quote today.
        opacity: feature.active ? 1 : 0.55,
      }}
      data-testid={`feature-row-${feature.id}`}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Box component="span" sx={{ display: "inline-flex", color: "success.main", flexShrink: 0 }}>
          <GitBranch size={14} />
        </Box>
        <Box
          component="span"
          sx={{ fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {feature.name}
        </Box>
        {!feature.active && (
          <Typography
            component="span"
            sx={{ fontSize: 12, color: "text.disabled", whiteSpace: "nowrap" }}
          >
            · Retired
          </Typography>
        )}
        {LICENSABLE_CATEGORIES.has(feature.category) &&
          (feature.licence_key ? (
            <Tooltip title={`Linked to the appliance feature ${feature.licence_key} — the key its licence file names.`}>
              <Chip
                size="small"
                variant="outlined"
                color="success"
                label={`Linked · ${feature.licence_key}`}
                data-testid="feature-linked"
                sx={{ height: 20, fontSize: 11, flexShrink: 0 }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Not linked to an appliance feature — it cannot go on a licence until it is. Edit the feature to link it.">
              <Chip
                size="small"
                variant="outlined"
                label="Not linked"
                data-testid="feature-unlinked"
                sx={{ height: 20, fontSize: 11, flexShrink: 0, color: "text.disabled" }}
              />
            </Tooltip>
          ))}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {feature.part_number && (
          <Box
            component="span"
            sx={{
              display: "inline-block",
              maxWidth: "100%",
              borderRadius: 0.5,
              bgcolor: "background.default",
              px: 0.75,
              py: 0.25,
              fontSize: 12,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {feature.part_number}
          </Box>
        )}
      </Box>

      <Typography
        component="span"
        sx={{
          fontSize: 12,
          color: "text.disabled",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {PRICE_LIST_CATEGORY_LABELS[feature.category]}
      </Typography>

      <Box sx={{ textAlign: "right", minWidth: 0 }}>
        {priced ? (
          <Typography
            component="span"
            sx={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}
          >
            {formatPriceListPrice(feature)}
          </Typography>
        ) : (
          <Tooltip title="Nobody has priced this yet — it is offered in no picker.">
            <Typography
              component="span"
              sx={{ fontSize: 12, color: "text.disabled", whiteSpace: "nowrap" }}
            >
              No price set
            </Typography>
          </Tooltip>
        )}
      </Box>

      {canEdit && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
          <IconButton
            size="small"
            color="inherit"
            aria-label={`Edit ${feature.name}`}
            onClick={props.onEdit}
          >
            <Pencil size={14} />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Delete ${feature.name}`}
            onClick={props.onDelete}
            sx={{ color: "error.main" }}
          >
            <Trash2 size={14} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
